import { z } from 'zod';
import { adresseSchema, energieetiketteSchema, feedRappenSchema, geoZahlUit, merkmalSchema, poortItemSchema, vermarktungImmobilienTypSchema, vermarktungVerfuegbarkeitArtSchema, DATUM_RE, } from './vermarktung-feed';
/**
 * Vermarktungs-WERKBANK — de projectie en de twee wallen (fase A, band A-1).
 *
 * Normbron: `docs/architecture/VERMARKTUNG-WERKBANK-FASE-A-ONTWERP.md` §2.2 +
 * `VERMARKTUNG-FEED-CONTRACT.md` §7. Waar dit bestand en het contract elkaar
 * raken wint het contract.
 *
 * DE TWEE WALLEN (§2.2 — niet één):
 *   1. **Naam-allowlist**: `werkbankProjektionSchema` is recursief `.strict()`,
 *      `VERBOTENE_FELDER` noemt de gevoelige dossiernamen van BEIDE kanten
 *      (kauf én miete/verwaltung) die nooit als SLEUTEL mogen opduiken, en
 *      `VERBOTEN_TOKENS` dekt daarnaast de niet-opsombare proza-categorie
 *      «mieter- en vertragsgegevens» van contract §7 plus de eigenaars- en
 *      notitie-namen van de fase-B-lijst (vier tokens). Beide kanten worden
 *      genormaliseerd (casefold én underscores weg), anders bewaakt de lijst
 *      een schrijfwijze die een `schluessel` niet eens kan dragen. Een vorm-pin
 *      loopt de zod-boom af.
 *   2. **Inhouds-poort**: namen bewaken sleutels, geen inhoud —
 *      `titel/beschreibung/highlights`, de merkmaal-waarden én de foto-`alt`
 *      zijn vrije tekst uit hetzelfde dossier waar ook de eigenaar en de
 *      Mindestpreis staan. {@link pruefeInhalt} weigert activatie als zo'n
 *      tekstveld de exacte waarde van een gevoelig dossierveld bevat.
 *
 * DE VOLLEDIGHEIDSPOORT IS HET FEED-SCHEMA (contract §7), niet een tweede
 * veldenlijst: {@link pruefeKomplettheit} draait `poortItemSchema.safeParse` op
 * de uitvoer van {@link poortItemUit} en voegt er de twee niet-schema-eisen aan
 * toe. {@link itemUit} is dezelfde kern plus de acht na-poort-velden (vijf
 * sinds v1, drie sinds contract v2 van 14-10). Zo kan de poort niet driften van
 * wat er uiteindelijk gepubliceerd wordt.
 */
// ────────────────────────── De projectie (§2.2 r.64) ────────────────────────
/**
 * Het adres in de WERKBANK: dezelfde vier velden en dezelfde vormregels als
 * `adresseSchema` uit het feed-bestand (contract §2.3), maar elk veld
 * optioneel.
 *
 * Waarom `.partial()`: de projectie is bewerkbare werkbank-toestand, niet het
 * artefact. Ontwerp §2.3 zegt met zoveel woorden «wat leeg of vormloos blijft,
 * blijft in de werkbank leeg en blokkeert de poort tot invoer» — een half
 * ingevuld object moet dus opgeslagen kunnen worden. Wat er WEL staat, staat er
 * in contractvorm (`plz` `^\d{4}$`, `kanton` als Kürzel in hoofdletters); de
 * stuur-actie schrijft alleen genormaliseerde, geldige waarden en laat de rest
 * leeg. De poort (`adresseSchema`, alle vier verplicht) is de wal.
 */
export const werkbankAdresseSchema = adresseSchema.partial();
/**
 * Ontwerp §2.2 — DE publieke inhoud van een werkbank-object. Recursief
 * `.strict()`: een onbekende sleutel is een weigering, geen strip, en de
 * allowlist-mutant wordt ook op een GENEST niveau geïnjecteerd
 * (`vermarktung.test.ts`).
 *
 * Bijna alles is optioneel — dat is geen slordigheid maar het model: de
 * werkbank mag onvolledig zijn, de FEED nooit. Verplicht zijn alleen de drie
 * velden die zonder waarde geen betekenis hebben: `preisAufAnfrage` (een
 * boolean heeft geen "leeg"), en de twee lijsten `highlights`/`merkmale` (leeg
 * = `[]`, nooit afwezig — contract §2.3 eist `highlights` altijd aanwezig).
 */
export const werkbankProjektionSchema = z
    .object({
    titel: z.string().trim().max(160).optional(),
    /**
     * Contract v2 (14-10, O-6) — de korte teaser naast `beschreibung`, ten
     * hoogste 300 tekens. Zoals `titel` hier zonder `min(1)`: de werkbank mag
     * leeg zijn, en een LEGE kurzbeschreibung bestaat in de feed niet
     * ({@link poortItemUit} laat hem dan weg — het feed-schema eist 1–300).
     */
    kurzbeschreibung: z.string().trim().max(300).optional(),
    beschreibung: z.string().trim().max(20_000).optional(),
    highlights: z.array(z.string().trim().min(1).max(200)).max(10),
    immobilienTyp: vermarktungImmobilienTypSchema.optional(),
    zimmer: z.number().nonnegative().max(100).optional(),
    badezimmer: z.number().int().nonnegative().max(99).optional(),
    /** Vrije korte tekst («2. OG links») — wint van `etagen` in `stockwerk`. */
    etage: z.string().trim().min(1).max(60).optional(),
    /** Aantal verdiepingen van het gebouw; terugval voor `stockwerk`. */
    etagen: z.number().int().nonnegative().max(200).optional(),
    baujahr: z.number().int().min(1000).max(2200).optional(),
    letztesRenovationsjahr: z.number().int().min(1000).max(2200).optional(),
    verfuegbarkeit: vermarktungVerfuegbarkeitArtSchema.optional(),
    /** Werkbank-eigen veld: de checklist-enum kent geen datum (ontwerp §1). */
    verfuegbarPerDatum: z.string().regex(DATUM_RE).optional(),
    nettowohnflaeche: z.number().nonnegative().max(1_000_000).optional(),
    /**
     * Contract §2.3 noemt `flaechen.nutzflaecheM2`, `flaechenSchema` draagt hem
     * en de checklist HEEFT hem (`flaechenSectionSchema.nutzflaeche`) — alleen
     * de projectie kende hem niet, waardoor het veld aan de kauf-kant geen
     * PRODUCENT had: een `gewerbe`- of `mfh`-object gaf de Nutzfläche in het
     * dossier op en die haalde de listing nooit. De totaliteits-pin loopt
     * projectie → item en zag dat gat niet (hij meet de andere richting niet).
     * Zelfde vorm als `nettowohnflaeche`; de decimalen-regel zit in `m2Schema`
     * aan de poortkant.
     */
    nutzflaeche: z.number().nonnegative().max(1_000_000).optional(),
    grundstuecksflaeche: z.number().nonnegative().max(1_000_000).optional(),
    balkonflaeche: z.number().nonnegative().max(1_000_000).optional(),
    garagenparkplaetze: z.number().int().nonnegative().max(999).optional(),
    aussenparkplaetze: z.number().int().nonnegative().max(999).optional(),
    /** Contract §3: Rappen-Int, zodat de mapping naar het item identiteit is. */
    preisRappen: feedRappenSchema.optional(),
    preisAufAnfrage: z.boolean(),
    adresse: werkbankAdresseSchema,
    merkmale: z.array(merkmalSchema).max(50),
    energieetikette: energieetiketteSchema.optional(),
})
    .strict();
/**
 * Ontwerp §2.1 — de fotoselectie: een subset van de clean `MandatePhoto`s, in
 * array-volgorde, met precies één titelbild.
 *
 * `titelbildPhotoId` is nulbaar omdat een vers object nog geen foto's heeft;
 * zodra er foto's zijn MOET hij er precies één aanwijzen (contract §2.3:
 * «precies één `titelbild: true` als er bilder zijn»). Naamgeving volgt het
 * contract (`titelbild`, §2.2) en niet de vroege ontwerpschets (`heroPhotoId`):
 * `hero` is in contract §5 de naam van een beeld-VARIANT, en dezelfde term voor
 * twee dingen is precies de drift die dit contract uitsluit.
 */
export const fotoAuswahlSchema = z
    .object({
    titelbildPhotoId: z.string().min(1).max(64).nullable(),
    fotos: z
        .array(z
        .object({
        photoId: z.string().min(1).max(64),
        alt: z.string().trim().min(1).max(300).optional(),
    })
        .strict())
        .max(60),
})
    .strict()
    .superRefine((auswahl, ctx) => {
    const ids = auswahl.fotos.map((f) => f.photoId);
    if (new Set(ids).size !== ids.length) {
        ctx.addIssue({ code: 'custom', path: ['fotos'], message: 'foto_doppelt' });
    }
    if (auswahl.fotos.length === 0) {
        if (auswahl.titelbildPhotoId !== null) {
            ctx.addIssue({
                code: 'custom',
                path: ['titelbildPhotoId'],
                message: 'titelbild_ohne_fotos',
            });
        }
        return;
    }
    const treffers = ids.filter((id) => id === auswahl.titelbildPhotoId).length;
    if (treffers !== 1) {
        ctx.addIssue({
            code: 'custom',
            path: ['titelbildPhotoId'],
            message: 'genau_ein_titelbild',
        });
    }
});
/** De Unicode-combineertekens die `normalize('NFD')` losmaakt (U+0300–U+036F).
 *  Als `new RegExp` opgeschreven zodat de bron zelf ASCII blijft — een
 *  combineerteken in een regex-literal is in een diff onzichtbaar.
 *  Gedeeld door {@link normaliseerInhalt} (wal 2) en {@link slugUit}. */
const DIAKRIET_RE = new RegExp(`[${String.fromCharCode(0x300)}-${String.fromCharCode(0x36f)}]`, 'g');
/**
 * Normalisatie voor de inhouds-vergelijking: casefold, DIAKRIETEN weg,
 * cijfergroepering weg en witruimte samengetrokken.
 *
 * De scheider wordt alleen TUSSEN twee cijfers verwijderd — zo wordt
 * `CHF 1'250'000` en `1 250 000` allebei `1250000`, terwijl een punt in gewone
 * tekst blijft staan en geen woorden aan elkaar plakt (dat zou vals-positieven
 * maken op een gate die activatie blokkeert). Werkt ook voor telefoons:
 * `+41 79 123 45 67` wordt `+41791234567`.
 *
 * De diakriet-strip (NFD, U+0300–U+036F weg) loopt over NAALD én HOOIBERG, en
 * dat is de hele reden dat hij werkt: het dossier draagt `Müller`, de
 * (deels LLM-gegenereerde) publicatietekst schrijft even makkelijk `Muller` —
 * één van de twee strippen zou de vergelijking juist kapotmaken. Umlauten
 * worden hier NIET uitgeschreven (`ü`→`ue`), anders dan in {@link slugUit}:
 * daar is de uitkomst een URL en telt de Duitse transliteratie, hier is het een
 * vergelijking en zou `ue`↔`u` twee verschillende woorden aan elkaar knopen.
 */
function normaliseerInhalt(waarde) {
    return waarde
        .toLowerCase()
        .normalize('NFD')
        .replace(DIAKRIET_RE, '')
        .replace(/(?<=\d)[.,'’\s](?=\d)/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}
/**
 * Een Zwitsers nummer heeft drie schrijfwijzen van dezelfde reeks cijfers, en
 * het dossier draagt er maar één: `+41 79 123 45 67` in de checklist tegenover
 * `079 123 45 67` in de listing is hetzelfde nummer en werd gemeten NIET
 * gevonden. Elke bron levert daarom al zijn schrijfwijzen als naald.
 *
 * Puur en O(1): één regex op de al genormaliseerde waarde, geen tabel, geen
 * bibliotheek. Wat niet als CH-nummer leest houdt zijn ene naald.
 */
const CH_NUMMER_RE = /^(?:\+41|0041|0)(\d{9})$/;
function naaldVarianten(naald) {
    const kern = CH_NUMMER_RE.exec(naald)?.[1];
    if (kern === undefined)
        return [naald];
    return [`+41${kern}`, `0041${kern}`, `0${kern}`];
}
/** Onder deze lengte is een «treffer» ruis, geen lek (bv. een kanton-Kürzel). */
const INHALT_MIN_LENGTE = 3;
/**
 * Wal 2 (ontwerp §2.2): weigert activatie als een vrij tekstveld van de
 * projectie óf van de FOTOSELECTIE de EXACTE waarde van een gevoelig
 * dossierveld bevat.
 *
 * Gemeten velden: `titel`, `beschreibung`, elke `highlights[i]`, elke
 * `merkmale.<i>.wert`, `etage` én elke `fotoAuswahl.fotos.<i>.alt` — alle vrije
 * tekst die (deels LLM-gegenereerd) uit hetzelfde dossier komt. Het ADRES wordt
 * bewust niet gemeten: dat is sinds owner-besluit V-4 publiek.
 *
 * DE ALT-TEKSTEN HOREN ERBIJ en stonden er tot fixronde 3 niet in: de
 * fotoselectie was niet eens een parameter, terwijl `alt` vrije stafinvoer is,
 * via {@link itemUit} ongefilterd in `bildItemSchema.alt` belandt en daarmee net
 * zo publiek is als de beschrijving. GEMETEN: met `eigentuemerEmail` in het
 * dossier en diezelfde tekst in een `alt` gaf deze wal `[]`.
 *
 * `fotoAuswahl` is VERPLICHT en bewust niet optioneel: een aanroeper die hem
 * vergeet zou de wal stil half-open zetten — precies het gebrek hierboven — en
 * verplicht maken is de enige vorm waarin dat vergeten in de TYPECHECK
 * zichtbaar wordt. Alle drie de aanroepers (`vermarktung.service.ts`) dragen de
 * selectie al in dezelfde regel: {@link pruefeKomplettheit} krijgt hem daar
 * naast deze functie mee.
 *
 * Retourneert de lijst treffers; leeg = poort groen.
 */
export function pruefeInhalt(projektion, dossier, fotoAuswahl) {
    const velden = [];
    if (projektion.titel !== undefined)
        velden.push({ veld: 'titel', tekst: projektion.titel });
    // Contract v2 (14-10): de teaser is net zo publiek als de beschrijving, en
    // net zo vrije tekst uit hetzelfde dossier — dus hoort hij achter dezelfde wal.
    if (projektion.kurzbeschreibung !== undefined) {
        velden.push({ veld: 'kurzbeschreibung', tekst: projektion.kurzbeschreibung });
    }
    if (projektion.beschreibung !== undefined) {
        velden.push({ veld: 'beschreibung', tekst: projektion.beschreibung });
    }
    if (projektion.etage !== undefined)
        velden.push({ veld: 'etage', tekst: projektion.etage });
    projektion.highlights.forEach((h, i) => velden.push({ veld: `highlights.${i}`, tekst: h }));
    projektion.merkmale.forEach((m, i) => velden.push({ veld: `merkmale.${i}.wert`, tekst: m.wert }));
    // De veldnaam draagt DEZELFDE punt-index als `highlights.0` hierboven —
    // `fotoAuswahl.fotos.0.alt`, precies de vorm die contract §7 documenteert
    // (`bilder.2.alt`). Er is bewust één vorm: een tweede, «mooiere» haakvorm zou
    // elke lezer (CRM-scherm, A-3-publisher, fase B) dwingen twee schrijfwijzen
    // voor hetzelfde begrip te kennen. Wat hier WEL afwijkt is de WORTEL van het
    // pad: `fotoAuswahl.…` wijst niet naar een veld van de projectie maar naar de
    // fotoselectie, die in een eigen kolom woont — vandaar de eigen prefix.
    // (Er stond dat de index «tussen haken» zou staan; de code levert de
    // punt-index. Band A-2, nit uit de order.)
    fotoAuswahl.fotos.forEach((foto, i) => {
        if (foto.alt !== undefined) {
            velden.push({ veld: `fotoAuswahl.fotos.${i}.alt`, tekst: foto.alt });
        }
    });
    const bronnen = [];
    for (const bron of Object.keys(dossier)) {
        const rauw = dossier[bron];
        if (rauw === null || rauw === undefined)
            continue;
        const naald = normaliseerInhalt(typeof rauw === 'number' ? String(rauw) : String(rauw));
        if (naald.length < INHALT_MIN_LENGTE)
            continue;
        bronnen.push({ bron, naalden: naaldVarianten(naald) });
    }
    const treffers = [];
    for (const veld of velden) {
        const hooiberg = normaliseerInhalt(veld.tekst);
        if (hooiberg.length === 0)
            continue;
        for (const bron of bronnen) {
            // ÉÉN treffer per (veld, bron): de schrijfwijzen zijn varianten van
            // dezelfde bron, niet drie lekken.
            if (bron.naalden.some((naald) => hooiberg.includes(naald))) {
                treffers.push({ veld: veld.veld, bron: bron.bron });
            }
        }
    }
    return treffers;
}
// ──────────────────────── Hulpregels van de stuur-actie ─────────────────────
/**
 * Contract §2.3 / `kernSchema.highlights` — een highlight is ten hoogste 200
 * tekens. Hier opgeschreven omdat {@link splitsHighlights} de enige producent is
 * die de grens moet HALEN in plaats van hem te meten.
 */
const HIGHLIGHT_MAX = 200;
/**
 * De zinsgrens: een `.`, `!` of `?` gevolgd door witruimte. Met een capturende
 * groep gesplitst, zodat de witruimte zelf in de uitkomst blijft staan
 * (`['zin.', ' ', 'zin.']`) — twee zinnen die in ÉÉN stuk terechtkomen houden zo
 * hun oorspronkelijke bytes; alleen op een echte knip valt de scheider weg.
 */
const ZINSGRENS_RE = /(?<=[.!?])(\s+)/;
/**
 * Splitst één stuk dat langer is dan {@link HIGHLIGHT_MAX} op zinsgrens, en voegt
 * hele zinnen greedy samen tot stukken van ten hoogste 200 tekens.
 *
 * NOOIT STIL AFKAPPEN — dat is de hele reden dat deze functie bestaat. De
 * checklist-bron (`objekt.highlights`, `schemas/pipeline.ts`) is ONBEGRENSDE
 * vrije tekst en het veld nodigt uit tot één alinea; een `.slice(0, 200)` zou
 * die alinea midden in een woord doorknippen en de afgeknipte helft stil
 * weggooien. Wat niet in een zin past blijft daarom staan: een ENKELE zin die
 * zelf langer is dan 200 tekens gaat ongewijzigd door en valt in de 422 van
 * `projektionUitDossier` — daar ziet de staf wélk veld te lang is, hier zou hij
 * niets zien.
 */
function splitsOpZinsgrens(stuk) {
    return greedyOpGrens(stuk, ZINSGRENS_RE);
}
/**
 * De komma-terugval (fix 20-10, «Zur Vermarktung senden» in prod): een stuk dat
 * NA de zinsgrens nog langer is dan 200 tekens is in de praktijk een
 * opsomming met komma's (gemeten: vier dossiers met één regel van 398–593
 * tekens, 11–15 komma's, geen zinseinde). Gesplitst op komma + witruimte — de
 * gevangen scheider (`, `) blijft in een samengevoegd stuk staan en valt alleen
 * op een echte knip weg, zodat geen highlight op een komma eindigt. Een komma
 * ZONDER witruimte (`3,5 Zimmer`) is geen scheider. Een komma-vrij fragment dat
 * zelf langer is dan 200 blijft staan en valt in de 422, zoals vandaag.
 */
const KOMMA_RE = /(,\s+)/;
function splitsOpKomma(stuk) {
    return greedyOpGrens(stuk, KOMMA_RE);
}
/**
 * Splitst één stuk dat langer is dan {@link HIGHLIGHT_MAX} op `grens` (een
 * regex met ÉÉN capturende groep: de scheider zelf) en voegt de delen greedy
 * samen tot stukken van ten hoogste 200 tekens; in een samengevoegd stuk komt
 * de oorspronkelijke scheider terug, op een knip valt hij weg.
 */
function greedyOpGrens(stuk, grens) {
    if (stuk.length <= HIGHLIGHT_MAX)
        return [stuk];
    const delen = stuk.split(grens);
    const uit = [];
    let huidig = '';
    for (let i = 0; i < delen.length; i += 2) {
        const zin = delen[i] ?? '';
        if (zin.length === 0)
            continue;
        if (huidig.length === 0) {
            huidig = zin;
            continue;
        }
        const samen = `${huidig}${delen[i - 1] ?? ' '}${zin}`;
        if (samen.length <= HIGHLIGHT_MAX) {
            huidig = samen;
            continue;
        }
        uit.push(huidig);
        huidig = zin;
    }
    if (huidig.length > 0)
        uit.push(huidig);
    return uit;
}
/**
 * Ontwerp §2.3 — `objekt.highlights` is in de checklist een STRING; de feed wil
 * een array van maximaal tien, elk ten hoogste 200 tekens. Splitst op
 * regeleinden, op de twee opsommingstekens die de stafteksten gebruiken (`·`,
 * `•`) en — sinds 20-10 — op de PUNTKOMMA (een puntkomma-lijst is een lijst),
 * trimt, laat lege stukken vallen, splitst wat dán nog te lang is verder op
 * ZINSGRENS ({@link splitsOpZinsgrens}) en, als laatste redmiddel, op KOMMA
 * ({@link splitsOpKomma}). Leeg ⇒ `[]` (nooit `undefined`: contract §2.3 eist
 * `highlights` altijd aanwezig).
 *
 * DE METING DIE PUNTKOMMA EN KOMMA BRACHT (prod 20-10, alleen lengtes): 9 van
 * 15 mandaatdossiers vielen bij «Zur Vermarktung senden» op `highlights.0`
 * too_big — vier komma-lijsten van 398–593 tekens zonder regeleinde of
 * zinseinde, vier puntkomma-lijsten van 266–523 tekens en één regel van 241
 * tekens met één puntkomma. Met beide regels komen ze allemaal door; een
 * dossier met elf puntkomma-items blijft een zichtbare 422 (welke tien, is een
 * menselijke keuze).
 *
 * SLICET NIET MEER OP TIEN — hij levert ALLES (fixronde 3). Die `.slice(0, 10)`
 * was precies de stille afkapping die de kop van {@link splitsOpZinsgrens}
 * verbiedt, en dat is gemeten: één alinea van 2057 tekens met dertien zinnen gaf
 * tien stukken en gooide er 477 tekens uit — geen fout, geen gebrek, geen spoor.
 * Diezelfde alinea ZÓNDER zinsgrenzen gaf wél een zichtbare 422: dezelfde
 * invoerklasse, tegengesteld gedrag.
 *
 * De tien-grens wordt afgedwongen waar hij hoort — `werkbankProjektionSchema`
 * (`highlights` `.max(10)`, contract §2.3). Meer dan tien stukken geeft dus een
 * 422 `{veld: 'highlights', reden: 'too_big'}`: zichtbaar en corrigeerbaar voor
 * de staf, terwijl stil verlies dat niet is.
 */
export function splitsHighlights(tekst) {
    if (tekst === null || tekst === undefined)
        return [];
    return tekst
        .split(/[\r\n·•;]+/)
        .map((deel) => deel.trim())
        .filter((deel) => deel.length > 0)
        .flatMap(splitsOpZinsgrens)
        .flatMap(splitsOpKomma);
}
const UMLAUT_TABEL = Object.freeze([
    [/ä/g, 'ae'],
    [/ö/g, 'oe'],
    [/ü/g, 'ue'],
    [/ß/g, 'ss'],
]);
/**
 * Contract §2.3 — de publieke URL-slug, kebab-case `^[a-z0-9]+(-[a-z0-9]+)*$`,
 * maximaal 80 tekens.
 *
 * Umlauten worden UITGESCHREVEN (ä→ae, ö→oe, ü→ue, ß→ss) vóór de
 * diakriet-strip, want `ü` naar `u` zou van «Grüningen» «Gruningen» maken —
 * dat is de Duitse transliteratie die elk CH-portaal voert. Overige diakrieten
 * (é, à, ç, …) gaan via NFD weg. Blijft er niets over, dan is de slug
 * `objekt`: een lege slug zou de regex breken en de rij onvindbaar maken.
 */
export function slugUit(titel, ort) {
    const rauw = [titel ?? '', ort ?? ''].filter((deel) => deel.trim().length > 0).join(' ');
    let tekst = rauw.toLowerCase();
    for (const [patroon, vervanging] of UMLAUT_TABEL)
        tekst = tekst.replace(patroon, vervanging);
    const slug = tekst
        .normalize('NFD')
        .replace(DIAKRIET_RE, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 80)
        .replace(/-+$/g, '');
    return slug.length > 0 ? slug : 'objekt';
}
/**
 * De vaste merkmaal-sleutels van de vier numerieke projectie-velden die géén
 * eigen item-veld hebben (ontwerp §2.2). Vaste sleutels, zodat de lezer
 * (WordPress-plugin) ze kan herkennen; `^[a-z0-9_]+$` per contract §2.3.
 */
export const PROJEKTION_MERKMAL_SCHLUESSEL = Object.freeze({
    badezimmer: 'badezimmer',
    garagenparkplaetze: 'garagenparkplaetze',
    aussenparkplaetze: 'aussenparkplaetze',
    letztesRenovationsjahr: 'letztes_renovationsjahr',
});
/**
 * TOTALITEITS-PIN, helft 1 (contract §7): waar elk projectie-veld landt. Een
 * eigen item-veld, of `merkmale`. `vermarktung.test.ts` eist dat deze tabel
 * exact de sleutels van `werkbankProjektionSchema.shape` dekt — een nieuw veld
 * dat nergens landt maakt de pin rood.
 */
export const PROJEKTION_BESTEMMING = Object.freeze({
    titel: 'titel',
    kurzbeschreibung: 'kurzbeschreibung',
    beschreibung: 'beschreibung',
    highlights: 'highlights',
    immobilienTyp: 'immobilienTyp',
    zimmer: 'zimmer',
    badezimmer: 'merkmale',
    etage: 'stockwerk',
    etagen: 'stockwerk',
    baujahr: 'baujahr',
    letztesRenovationsjahr: 'merkmale',
    verfuegbarkeit: 'verfuegbarkeit',
    verfuegbarPerDatum: 'verfuegbarkeit',
    nettowohnflaeche: 'flaechen',
    nutzflaeche: 'flaechen',
    grundstuecksflaeche: 'flaechen',
    balkonflaeche: 'flaechen',
    garagenparkplaetze: 'merkmale',
    aussenparkplaetze: 'merkmale',
    preisRappen: 'preisRappen',
    preisAufAnfrage: 'preisAufAnfrage',
    adresse: 'adresse',
    merkmale: 'merkmale',
    energieetikette: 'energieetikette',
});
/**
 * TOTALITEITS-PIN, helft 2 (contract §7): de acht item-velden die NIET uit de
 * poort-invoer komen, elk met zijn benoemde bron. Alles wat hier niet staat
 * moet uit {@link poortItemUit} komen. De drie onderste kwamen er met contract
 * v2 (14-10) bij en komen alle drie uit de RIJ.
 */
export const NA_POORT_BRON = Object.freeze({
    id: 'rij',
    slug: 'rij',
    stand: 'rij',
    exposeAnfrage: 'expose',
    bilder: 'varianten',
    status: 'rij',
    veroeffentlichtAm: 'rij',
    referenz: 'rij',
});
function voegMerkmaalToe(merkmale, schluessel, waarde) {
    if (waarde === undefined)
        return;
    if (merkmale.some((m) => m.schluessel === schluessel))
        return;
    merkmale.push({ schluessel, wert: String(waarde) });
}
/**
 * De KERN van de afbeelding: alles wat uit de projectie plus de geocode volgt —
 * dus het feed-item MINUS de acht na-poort-velden (`id`, `slug`, `stand`,
 * `exposeAnfrage`, `bilder`, `status`, `veroeffentlichtAm`, `referenz` —
 * contract §7). Dit is precies de vorm die `poortItemSchema` beschrijft.
 *
 * Bewust ONVALIDEERD (`Record<string, unknown>`): een onvolledige projectie MOET
 * hier doorheen kunnen, want de uitkomst is juist wat de poort meet. Wie
 * publiceert, parst met `feedItemSchema` — dat is de enige weg naar de draad.
 *
 * ⚠️ Afwijking van de signatuur in de bouwbrief (`(projektion, fotoAuswahl,
 * geocode)`): de fotoselectie levert geen enkel poort-veld — `bilder` is een van
 * de weggelaten velden — en een ongebruikte parameter in een gedeelde API
 * is een leugen over de afbeelding. De fotoselectie hoort bij {@link itemUit} en
 * bij {@link pruefeKomplettheit} (de niet-schema-eis), en daar staat ze ook.
 */
export function poortItemUit(projektion, geocode) {
    // merkmale: eerst de vrije paren van de werkbank, dan de vier afgeleide met
    // vaste sleutels — deterministische volgorde, want de bytes worden gehasht.
    const merkmale = projektion.merkmale.map((m) => ({
        schluessel: m.schluessel,
        wert: m.wert,
    }));
    voegMerkmaalToe(merkmale, PROJEKTION_MERKMAL_SCHLUESSEL.badezimmer, projektion.badezimmer);
    voegMerkmaalToe(merkmale, PROJEKTION_MERKMAL_SCHLUESSEL.garagenparkplaetze, projektion.garagenparkplaetze);
    voegMerkmaalToe(merkmale, PROJEKTION_MERKMAL_SCHLUESSEL.aussenparkplaetze, projektion.aussenparkplaetze);
    voegMerkmaalToe(merkmale, PROJEKTION_MERKMAL_SCHLUESSEL.letztesRenovationsjahr, projektion.letztesRenovationsjahr);
    const flaechen = {};
    if (projektion.nettowohnflaeche !== undefined) {
        flaechen.wohnflaecheM2 = projektion.nettowohnflaeche;
    }
    // De poort ÍS het feed-schema: wie een projectie-veld toevoegt zonder het hier
    // te mappen, laat het veld nergens landen (contract §2.3 `nutzflaecheM2`).
    if (projektion.nutzflaeche !== undefined) {
        flaechen.nutzflaecheM2 = projektion.nutzflaeche;
    }
    if (projektion.grundstuecksflaeche !== undefined) {
        flaechen.grundstuecksflaecheM2 = projektion.grundstuecksflaeche;
    }
    if (projektion.balkonflaeche !== undefined) {
        flaechen.balkonflaecheM2 = projektion.balkonflaeche;
    }
    // `etage` wint; anders `etagen` als tekst (ontwerp §2.2 mapping).
    const stockwerk = projektion.etage !== undefined
        ? projektion.etage
        : projektion.etagen !== undefined
            ? String(projektion.etagen)
            : null;
    const verfuegbarkeit = { art: projektion.verfuegbarkeit };
    if (projektion.verfuegbarkeit === 'per_datum' && projektion.verfuegbarPerDatum !== undefined) {
        verfuegbarkeit.datum = projektion.verfuegbarPerDatum;
    }
    const item = {
        transaktion: 'kauf',
        titel: projektion.titel,
        beschreibung: projektion.beschreibung,
        highlights: projektion.highlights,
        immobilienTyp: projektion.immobilienTyp,
        zimmer: projektion.zimmer ?? null,
        flaechen,
        baujahr: projektion.baujahr ?? null,
        stockwerk,
        verfuegbarkeit,
        adresse: {
            strasse: projektion.adresse.strasse,
            plz: projektion.adresse.plz,
            ort: projektion.adresse.ort,
            kanton: projektion.adresse.kanton,
        },
        // Het geo-recept van contract §3 is TWEE stappen, niet één — `toFixed(6)`
        // plus het wegstrepen van de negatieve nul; {@link geoZahlUit} doet ze
        // allebei en woont in het contract-bestand, naast de vorm die hij haalt.
        geo: geocode === null || geocode === undefined
            ? undefined
            : { lat: geoZahlUit(geocode.lat), lon: geoZahlUit(geocode.lon) },
        merkmale,
        preisAufAnfrage: projektion.preisAufAnfrage,
        energieetikette: projektion.energieetikette ?? null,
    };
    // Contract §2.4: afwezig, niet `0` en niet `null`, als de prijs op aanvraag is.
    if (projektion.preisRappen !== undefined)
        item.preisRappen = projektion.preisRappen;
    // Contract v2 (O-6): de teaser, of NIETS. Een lege (of alleen-witruimte)
    // tekst is in de werkbank «nog niet ingevuld» en in de feed geen waarde — het
    // feed-schema eist 1–300 tekens, dus `''` zou het hele item laten vallen.
    const kurz = projektion.kurzbeschreibung?.trim();
    if (kurz !== undefined && kurz.length > 0)
        item.kurzbeschreibung = kurz;
    return item;
}
/**
 * De ENE pure afbeelding projektion → feed-item die de publisher (A-3) gebruikt
 * (contract §7). {@link poortItemUit} plus de acht na-poort-velden, elk uit zijn
 * benoemde bron ({@link NA_POORT_BRON}).
 *
 * `varianten` is op `photoId` gesleuteld, niet op volgorde: de VOLGORDE komt uit
 * `fotoAuswahl.fotos` (ontwerp §2.1 — array-volgorde is de galerijvolgorde), en
 * een foto zonder gerenderde variant valt er stil uit in plaats van een gat in
 * het artefact te slaan. Valt daarbij het TITELBILD weg, dan promoveert
 * {@link normaliseerTitelbild} de eerste overgebleven foto — zie daar waarom.
 *
 * ── STATUS VÓÓR PREIS (contract v2, O-10) ──────────────────────────────────
 * Een `verkauft`-object staat in de feed ZONDER bedrag en met
 * `preisAufAnfrage: true` — ongeacht wat de projectie draagt. De projectie
 * blijft ongemoeid (de werkbank toont de prijs nog, en een terugdraai naar
 * `verfuegbar` brengt hem zonder herinvoer terug); alleen het ARTEFACT
 * verandert. De lezer rendert dan de marke in plaats van een prijs of een
 * «auf Anfrage»-tekst (contract §10).
 */
export function itemUit(rij, projektion, fotoAuswahl, geocode, varianten) {
    const item = poortItemUit(projektion, geocode);
    item.id = rij.id;
    item.slug = rij.slug;
    item.stand = new Date(rij.updatedAt).toISOString();
    item.exposeAnfrage = Boolean(rij.exposeAnfrage);
    item.status = rij.status;
    item.referenz = rij.referenz;
    if (rij.publiziertAt !== null) {
        item.veroeffentlichtAm = new Date(rij.publiziertAt).toISOString();
    }
    if (rij.status === 'verkauft') {
        delete item.preisRappen;
        item.preisAufAnfrage = true;
    }
    const bilder = [];
    for (const foto of fotoAuswahl.fotos) {
        const variant = varianten[foto.photoId];
        if (variant !== undefined)
            bilder.push(variant);
    }
    item.bilder = normaliseerTitelbild(bilder);
    return item;
}
/**
 * Contract §2.3: «precies één `titelbild: true` als er bilder zijn».
 *
 * Een foto zonder gerenderde variant valt hierboven stil weg — en als dat NET
 * het titelbild was, zou het hele item op `bilder:genau_ein_titelbild` rood
 * worden en zou één mislukte render de listing uit de feed slaan. De eerste
 * overgebleven foto wordt daarom titelbild: een galerij met een ander openings-
 * beeld is een schoonheidsfout, een verdwenen listing is er geen.
 *
 * Dezelfde regel repareert de andere kant (twee varianten met `titelbild: true`,
 * bv. na een half doorgevoerde herselectie): er blijft er exact één over.
 * Kopieert alleen wat hij verandert, zodat de bytes van een gezonde galerij
 * identiek blijven.
 */
function normaliseerTitelbild(bilder) {
    if (bilder.length === 0)
        return bilder;
    const eerste = bilder.findIndex((bild) => bild.titelbild);
    const kop = eerste === -1 ? 0 : eerste;
    return bilder.map((bild, i) => bild.titelbild === (i === kop) ? bild : { ...bild, titelbild: i === kop });
}
/**
 * De VOLLEDIGHEIDSPOORT (ontwerp §2.2, contract §7): `poortItemSchema` op de
 * uitvoer van {@link poortItemUit}, plus de twee eisen die geen schemavorm
 * hebben — ≥ 1 vrijgegeven foto MÉT titelbild, en een geslaagde geocode (V-6).
 *
 * Volgorde en ontdubbeling: de niet-schema-eisen komen eerst en een schema-issue
 * op hetzelfde `veld` wordt daarna overgeslagen. Zonder die regel zou een
 * ontbrekende geocode twee regels geven (`geo/custom` én `geo/invalid_type`) en
 * zou de werkbank-UI hetzelfde gebrek twee keer tonen.
 *
 * `reden` is de zod `issue.code` — hetzelfde bounded token als in
 * `publikationsFehler` en in de audit-metadata, nooit `issue.message`
 * (contract §7: de allowlist weigert prosa).
 */
export function pruefeKomplettheit(projektion, fotoAuswahl, geocode) {
    const gebreken = [];
    if (geocode === null || geocode === undefined) {
        gebreken.push({ veld: 'geo', reden: 'custom' });
    }
    const titelbild = fotoAuswahl.fotos.some((f) => f.photoId === fotoAuswahl.titelbildPhotoId && fotoAuswahl.titelbildPhotoId !== null);
    if (fotoAuswahl.fotos.length < 1 || !titelbild) {
        gebreken.push({ veld: 'bilder', reden: 'custom' });
    }
    const gedekt = new Set(gebreken.map((g) => g.veld));
    const uitkomst = poortItemSchema.safeParse(poortItemUit(projektion, geocode));
    if (!uitkomst.success) {
        for (const issue of uitkomst.error.issues) {
            const veld = issue.path.map((deel) => String(deel)).join('.');
            if (gedekt.has(veld))
                continue;
            gebreken.push({ veld, reden: issue.code });
        }
    }
    return gebreken;
}
/**
 * De foto's die het exposé/de publicatie consumeert (architectuurdoc §5): alleen
 * clean scans; de `useForExpose`-selectie in galerijvolgorde — of, zolang er
 * niets geselecteerd is, ALLE clean foto's (de gedocumenteerde terugval).
 * Invoer wordt in galerijvolgorde verwacht (de API sorteert op `sortOrder`) en
 * die volgorde blijft behouden.
 *
 * Sinds A-1 woont deze regel HIER en niet meer in het CRM: de stuur-actie
 * (backend, §2.3) bouwt de default-fotoselectie met exact dezelfde regel als het
 * scherm, en twee definities zouden op den duur twee selecties zijn.
 * `apps/crm/src/pages/mandates/gallery.dedup.test.ts` pint dat het CRM de
 * shared-versie doorexporteert in plaats van een eigen kopie te houden.
 */
export function exposeGallerySelection(photos) {
    const clean = photos.filter((p) => p.scanStatus === 'clean');
    const selected = clean.filter((p) => p.useForExpose);
    return selected.length > 0 ? selected : clean;
}
// ─────────────── De CRM-lijst «wie heeft dit object opgevraagd» ──────────────
/**
 * `GET /vermarktung/objekte/:id/anfragen` — de leesroute achter het
 * CRM-aanvragenlijstje (band A-4; ontwerp §2.8 noemt hem bij naam: het is
 * waarvóór `expose_anfragen` zijn `@@index([objektId])` draagt).
 *
 * WAAROM DEZE SCHEMA'S HIER STAAN EN NIET IN `schemas/expose-anfrage.ts`. Dat
 * bestand is de PUBLIEKE draad van de mailgate — edge en backend lezen hem, het
 * CRM niet. Dit bestand is de WERKBANK: het hangt in beide barrels omdat een
 * browserbundel het leest, en dít is wat het CRM-scherm ophaalt. Twee
 * verschillende lezerskringen in één bestand is precies de drift die het
 * feed-contract uitsluit (zie de kop van `expose-anfrage.ts`, die dezelfde
 * scheiding vanaf de andere kant beschrijft).
 *
 * WAT ER NADRUKKELIJK NIET IN STAAT: het token of zijn hash. De aanvraagrij
 * draagt `tokenHash @unique`, en dát is de lookup-sleutel van een LEVEND
 * credential — hem aan een CRM-scherm geven zou van elke browser-devtools een
 * sleutelbos maken. De `.strict()` hieronder is daarvoor de wal: een
 * `select` die er ooit een veld bij zet valt om op de parse in plaats van
 * stilletjes over de draad te gaan.
 */
export const exposeAnfrageZeileSchema = z
    .object({
    id: z.string().uuid(),
    leadId: z.string().uuid(),
    /** Het adres van de aanvrager, uit de LEAD. */
    email: z.string().email().max(254),
    /**
     * `firstName` + `lastName` van de lead, samengevoegd. GEEN `min(1)`: beide
     * kolommen zijn in het CRM-schema verplicht en niet-leeg, maar een lege
     * string uit een oude of geïmporteerde rij mag een lijstroute niet met een
     * 500 laten omvallen — een lege naam is eerlijker dan geen lijst.
     */
    name: z.string().max(240),
    /** `createdAt` van de aanvraag — wanneer er om het document gevraagd is. */
    angefragtAt: z.string().datetime(),
    /** De KLIK (`POST /ingest/expose/bestaetigen`), niet de uitlevering. */
    erstesOeffnenAt: z.string().datetime().nullable(),
    /**
     * `leads.emailVerifiedAt` — HET ADRES IS BEWEZEN. Een feit over de LEAD, en
     * daarmee het enige veld van deze rij dat NIET over deze aanvraag gaat.
     *
     * WAAROM HET ER SINDS 11-9 IN STAAT (masterbesluit op MAJOR-1 van het
     * A-4-verdict). Klikt iemand op zijn link nadat het object van de markt is,
     * dan blijft het adresbewijs verdiend maar krijgt de aanvraag GEEN
     * `erstesOeffnenAt` meer — `bestaetige` slaat die stempel over en antwoordt
     * 410. Zonder dit veld zou de lijst zo'n rij tonen als «Noch nicht
     * geöffnet», en dat is dan onwaar: er is wel degelijk geklikt. Twee feiten,
     * twee velden — dezelfde lijn als `erstesOeffnenAt` naast `widerrufenAt`.
     *
     * ⚠️ HET DRAAGT VOOR ELKE AANVRAAG VAN DEZELFDE LEAD DEZELFDE WAARDE, en dat
     * is juist: één belangstellende kan meerdere objecten opvragen, en zijn
     * adres is dan één keer bewezen — niet één keer per object. Het bewijs kan
     * bovendien van een HEEL ANDERE weg komen (de DOI van het Bewertungsbericht
     * zet dezelfde kolom), dus een gevulde waarde bewijst NIET dat er op déze
     * link geklikt is. Wat het scherm ermee doet moet dat zeggen: het label
     * hoort over het ADRES te gaan, nooit over «deze aanvraag is bevestigd».
     */
    emailBestaetigtAt: z.string().datetime().nullable(),
    widerrufenAt: z.string().datetime().nullable(),
    /**
     * AFGELEID uit `expiresAt` versus nu, en daarom staat de rauwe datum er
     * niet: «14.09.2026, 11:04» zegt een makelaar niets, «verlopen» wel. De
     * server rekent, zodat de klok van een browser het antwoord niet kan
     * veranderen.
     */
    abgelaufen: z.boolean(),
})
    .strict();
/**
 * De HARDE BOVENGRENS van de lijst. Een aanvragenlijstje dat stilletjes afkapt
 * liegt tegen de makelaar die er zijn opvolging op baseert; daarom is er een
 * plafond MET een `totaal` ernaast in plaats van een onbegrensde lijst of een
 * stille `take`. 200 is ruim boven elk realistisch aantal per object en houdt
 * het antwoord onder een paar tientallen kB.
 */
export const EXPOSE_ANFRAGEN_MAX = 200;
/**
 * Het antwoord: hoogstens {@link EXPOSE_ANFRAGEN_MAX} rijen, NIEUWSTE EERST, en
 * `totaal` = het ECHTE aantal aanvragen voor dit object. `items.length < totaal`
 * is dus het zichtbare signaal dat er is afgekapt.
 */
export const exposeAnfragenListeSchema = z
    .object({
    items: z.array(exposeAnfrageZeileSchema).max(EXPOSE_ANFRAGEN_MAX),
    totaal: z.number().int().nonnegative(),
})
    .strict();
